import React, { useEffect, useMemo } from 'react';
import { Route, Switch, useParams } from 'react-router-dom';
import { useCollect, useRequest } from 'hooks';

import DayJs from 'utils/Dayjs';
import { EXAM_STUDENT_STATES } from 'utils/Constants';

import Avatar from 'components/Misc/Avatar';
import Progress from 'components/Misc/Progress';
import Container from 'components/Misc/Container';
import { Title } from 'components/Misc/Typography';
import { Button, Input, Select } from 'components/Misc/Form';

import View from 'containers/Shared/Exams/Profile/Review/View';
import Tasks from 'containers/Shared/Exams/Profile/Review/Tasks';
import Distribution from 'containers/Shared/Exams/Profile/Tasks/Distribution';
import Duration from 'containers/Shared/Exams/Profile/Review/Widgets/Duration';

import { useExamReviewStore } from 'store/exam/useExamReviewStore';

const Review = ({ tab, setTab }) => {
	// Store current object's id
	const { takeId } = useParams();

	// Update selected tab on mount
	useEffect(() => setTab(tab), []);

	// Use exam's store
	const [take, tasks, student, summaries, answers, onInit, onReset, onChange] = useExamReviewStore(state => [
		state.take,
		state.tasks,
		state.student,
		state.summaries,
		state.answers,
		state.onInit,
		state.onReset,
		state.onChange
	]);

	const parameters = useMemo(() => ({
		eager: '[student.grade, summaries, exam.[subject, tasks.questions], course, answers.[question, options.option, words.word, files]]'
	}), []);

	// Store requested take
	const [[result, progress], collect] = useCollect(`takes/${takeId}`, parameters);

	// Store update request
	const [[updating], update] = useRequest('put', `takes/${takeId}`);

	// Collect result on mount and on parameter change
	useEffect(() => {
		(async () => await collect())();
	}, [collect]);

	// Initialize exam's data after collection
	useEffect(() => {
		if (result) onInit(result);

		// Reset exam's data on unmount
		return () => onReset();
	}, [result]);

	// Handle take update
	const onSubmit = async (event) => {
		event.preventDefault();

		// Do not submit twice
		if (updating) return;

		// Try to update take
		const [success] = await update({
			take,
			answers,
			summaries
		});
	};

	return (
		<div className="mt-4 space-y-4">
			<Container className="p-4 grid sm:grid-cols-2 gap-4">
				{/* Student information */}
				<div className="flex space-x-2">
					<Avatar
						alt={student.fullName}
						src={student.avatar}
					/>

					<Title
						title={student.fullName}
						subtitle={student.status}
						className="flex-1"
					/>

					<Button
						small
						square
						icon="link"
						to={`/users/${student.id}`}
					/>
				</div>

				<div className="space-y-2">
					{/* Take's start date */}
					<div className="space-y-1">
						<div className="text-sm text-neutral-700 dark:text-neutral-200">Sākuma laiks</div>
						<div className="text-neutral-800 dark:text-white">{DayJs(take.startedAt).format('D. MMM, YYYY HH:mm')}</div>
					</div>

					{/* Take's end date */}
					<div className="space-y-1">
						<div className="text-sm text-neutral-700 dark:text-neutral-200">Iesniegšanas laiks</div>
						<div className="text-neutral-800 dark:text-white">{DayJs(take.endedAt).format('D. MMM, YYYY HH:mm')}</div>
					</div>

					{/* Take's duration */}
					<Duration />
				</div>
			</Container>

			<Switch>
				{/* Exam's task */}
				<Route exact path="/exams/results/review/:id(\d+)/:takeId(\d+)/:taskId(\d+)">
					<View />
				</Route>
			</Switch>

			{/* Show progress */}
			<Progress progress={progress} />

			{/* Exam's tasks */}
			<Tasks />

			{/* Point distribution */}
			<Distribution tasks={tasks} />

			{/* Exam's review */}
			<Container className="py-3 px-4 flex flex-col sm:flex-row sm:justify-end space-y-4 sm:space-y-0 sm:space-x-4 sm:items-end">
				<Input
					type="number"
					name="score"
					label="Iegūtais punktu skaits"
					value={take.score || ''}
					onChange={onChange}
				/>

				<Input
					type="number"
					name="mark"
					label="Iegūtais vērtējums"
					value={take.mark || ''}
					onChange={onChange}
				/>

				<Select
					name="state"
					label="Statuss"
					value={take.state}
					onChange={onChange}
					options={EXAM_STUDENT_STATES.map(state => ({
						value: state.value, name: state.text
					}))}
				/>

				<div className="flex justify-end">
					<Button
						icon="done"
						theme="success"
						value="Saglabāt"
						progress={updating}
						onClick={onSubmit}
					/>
				</div>
			</Container>
		</div>
	);
};

export default Review;